import React, { useEffect, useRef } from 'react';
import usePreRedirectArgs from '../../../../../hooks/usePreRedirectArgs';
import { useResetConnectedHealthSystems } from '../../../../../hooks/api/useProfileConnectedHealthSystems';
import StripeProducts from './StripeProducts';
import { useNestedNavigation, useRootNavigation } from '../../../../../utils/appNavigationNames';
import { useIsFocused } from '@react-navigation/native';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { Platform, View } from 'react-native';
import useInitiateRefreshCapabilityDiscovery from '../../../../../hooks/api/useInitiateEndpointRefreshCapabilityDiscover';
import { resetCanAddMedicalProfileCache } from '../../../../../hooks/api/useCanAddMedicalProfile';
import usePendingFastenConnections from '../../../../../hooks/api/usePendingFastenConnections';
import useNotify from '../../../../../hooks/useNotify';
import { getIntl } from '../../../../../utils/intl';
import { notifyBugsnag } from '../../../../../utils/bugsnag';

/**
 * Fasten redirect params captured from the URL.
 * Must be set early (before React Navigation changes the URL) by calling captureFastenRedirectParams().
 */
let capturedFastenRedirectParams: {
    orgConnectionId?: string;
    error?: string;
    errorDescription?: string;
} = {};

/**
 * Capture Fasten redirect params.
 * - Web: reads from window.location.href (called at module level in App.tsx before React Navigation changes the URL)
 * - Mobile: accepts route params directly from the deep link (called from Root/RedirectScreen)
 */
export function captureFastenRedirectParams(routeParams?: Record<string, string>) {
    if (routeParams) {
        capturedFastenRedirectParams = {
            orgConnectionId: routeParams.org_connection_id || undefined,
            error: routeParams.error || undefined,
            errorDescription: routeParams.error_description || undefined,
        };
        return;
    }

    if (Platform.OS !== 'web') return;
    try {
        const url = new URL(window.location.href);
        capturedFastenRedirectParams = {
            orgConnectionId: url.searchParams.get('org_connection_id') || undefined,
            error: url.searchParams.get('error') || undefined,
            errorDescription: url.searchParams.get('error_description') || undefined,
        };
    } catch {
        // ignore
    }
}

const intl = getIntl('redirect-screen', {
    fastenOAuthError: 'Unable to connect. Please try again.',
    fastenNoResult: "We couldn't confirm the connection. If you finished signing in, it may take a moment to appear.",
});

export type RedirectScreenScreenOwnProps = {};

export type RedirectScreenScreenProps = RedirectScreenScreenOwnProps;

const RedirectScreenScreen: React.FC<RedirectScreenScreenProps> = () => {
    const notify = useNotify();
    const rootNavigation = useRootNavigation();
    const { nestedProfileNavigation, nestedOrganizationNavigation } = useNestedNavigation();
    const { isLoading, preRedirectArgs, clearPreRedirectArgs } = usePreRedirectArgs();
    const resetConnectedHealthSystems = useResetConnectedHealthSystems();
    const redirectAlreadyHandled = useRef(false);
    const isFocused = useIsFocused();
    const { initiateRefreshCapabilityDiscovery } = useInitiateRefreshCapabilityDiscovery();
    const initiateRefreshCapabilityDiscoveryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // When new preRedirectArgs arrive (e.g. a second redirect like reconnect), reset the
    // handled flag so the processing effect below can run again. This effect MUST be declared
    // before the processing effect because React runs effects in declaration order.
    useEffect(() => {
        if (preRedirectArgs) {
            redirectAlreadyHandled.current = false;
        }
    }, [preRedirectArgs]);

    // Fasten pending connections — profileId comes from preRedirectArgs
    const fastenProfileId = preRedirectArgs?.type === 'fasten-connect' ? preRedirectArgs.profileId : '';
    const { addPendingConnection } = usePendingFastenConnections({
        medicalProfileId: fastenProfileId,
        enabled: false,
    });

    useEffect(() => {
        if (isFocused && !isLoading && !redirectAlreadyHandled.current) {
            redirectAlreadyHandled.current = true;
            if (!preRedirectArgs) {
                // @ts-expect-error TS2345
                rootNavigation.navigate('app');
                return;
            }

            if (preRedirectArgs.type === 'portal-connect') {
                if (typeof preRedirectArgs.endpointId === 'number') {
                    initiateRefreshCapabilityDiscoveryTimeoutRef.current = setTimeout(() => {
                        initiateRefreshCapabilityDiscovery({
                            medicalProfileId: preRedirectArgs.profileId,
                            endpointId: preRedirectArgs.endpointId!,
                        });
                    }, 40000);
                }

                resetConnectedHealthSystems(preRedirectArgs.profileId);

                nestedProfileNavigation('connect', {
                    profileId: preRedirectArgs.profileId,
                    forcePortalRefresh: 'true',
                });
                clearPreRedirectArgs();
            } else if (preRedirectArgs.type === 'fasten-connect') {
                const params = { ...capturedFastenRedirectParams };
                capturedFastenRedirectParams = {}; // Clear after consumption to prevent stale reads

                if (params.error) {
                    console.error('Fasten OAuth error:', params.error, params.errorDescription);
                    notify('error', intl('fastenOAuthError'));
                } else if (params.orgConnectionId) {
                    // Save as pending connection — webhook will confirm it
                    addPendingConnection({
                        org_connection_id: params.orgConnectionId,
                        platform_type: preRedirectArgs.platformType || 'unknown',
                        brand_id: preRedirectArgs.brandId || '',
                        brand_name: preRedirectArgs.brandName || '',
                        connection_status: 'pending',
                        medicalProfileId: preRedirectArgs.profileId,
                    });
                } else {
                    // The redirect came back with neither an org_connection_id nor an error, so there is
                    // nothing to show optimistically. This is the signature of the deep-link params being
                    // lost on the round trip (e.g. a cold-start return). Tell the user instead of silently
                    // returning to Connect, and report it so we can measure how often the param is dropped.
                    notify('warn', intl('fastenNoResult'));
                    notifyBugsnag(new Error('Fasten connect redirect returned without org_connection_id or error'));
                }

                // Navigate to connections screen with pending alert
                nestedProfileNavigation('connect', {
                    profileId: preRedirectArgs.profileId,
                    showFastenPendingAlert: params.orgConnectionId ? 'true' : undefined,
                });
                clearPreRedirectArgs();
            } else if (preRedirectArgs?.type === 'stripe-connected-account') {
                nestedOrganizationNavigation('root', {
                    defaultTab: 'client-billing',
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFocused, isLoading, preRedirectArgs]);

    useEffect(() => {
        /**
         * The user can go back to the screen with the back button or by clicking the activity tab
         *
         * if the pre-redirect args were cleared, that means this redirect was already handled.  Instead
         * of being stuck with a loader, navigate immediately to the activity root screen
         */
        if (isFocused && !isLoading && !preRedirectArgs) {
            nestedProfileNavigation('root', undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFocused, isLoading, preRedirectArgs]);

    useEffect(() => {
        return () => {
            if (initiateRefreshCapabilityDiscoveryTimeoutRef.current) {
                clearTimeout(initiateRefreshCapabilityDiscoveryTimeoutRef.current);
            }
        };
    }, []);

    if (preRedirectArgs?.type === 'stripe') {
        return (
            <StripeProducts
                onContinue={() => {
                    clearPreRedirectArgs();
                    resetCanAddMedicalProfileCache();
                    nestedProfileNavigation('root', undefined);
                }}
            />
        );
    }

    return (
        <AnimateLoadedContent isLoading={true}>
            {() => <View style={{ width: '100%', height: '100%' }} />}
        </AnimateLoadedContent>
    );
};

export default React.memo(RedirectScreenScreen);
